import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { signOut } from '../lib/auth';
import { LogIn, LogOut, User, Settings, ChevronDown, Save, Menu, X } from 'lucide-react';
import { LoginModal } from './LoginModal';
import toast from 'react-hot-toast';
import { useOnboardingStore } from '../store';
import { autosaveOnboardingLead } from '../lib/leadCapture';
import { isAdminType } from '../lib/adminRoles';
import { saveOwnOnboardingProfile } from '../lib/onboardingProfile';

export function Header() {
  const navigate = useNavigate();
  const { user, adminType } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { currentStep, reset, client, selectedServiceTypes, selectedTier, selectedServices } = useOnboardingStore();
  const isAdmin = isAdminType(adminType);

  const handleLogout = async () => {
    const success = await signOut();
    if (success) {
      toast.success('Successfully logged out');
      navigate('/');
    } else {
      toast.error('Failed to log out');
    }
    setShowUserMenu(false);
    setShowMobileMenu(false);
  };

  const handleSaveAndExit = async () => {
    try {
      await autosaveOnboardingLead({
        draft: {
          ...(client as Record<string, unknown>),
          selectedServiceTypes,
          selectedTier,
          selectedServices,
        },
        currentStep,
        progressPercent: Math.round(((currentStep + 1) / 7) * 100),
        selectedServices,
        selectedPlatforms: [],
        userId: user?.id,
        changedFields: ['client', 'currentStep', 'selectedServiceTypes', 'selectedTier', 'selectedServices'],
      });
      
      if (user && !isAdmin) {
        await saveOwnOnboardingProfile({
          businessName: client.businessName || null,
          contactName: client.contactName || null,
          phone: client.phone || null,
          address: client.address || null,
          onboardingStep: currentStep,
        });
      }
      
      reset();
      navigate('/');
      toast.success('Progress saved successfully');
    } catch (error) {
      console.error('Failed to save progress:', error);
      toast.error('Failed to save progress');
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 w-full bg-scale-darker/50 backdrop-blur-md z-10 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="/images/logo.png"
                alt="Scale Small.AI"
                className="w-10 h-10 md:w-12 md:h-12"
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <span className="text-white font-bold text-lg md:text-xl">SCALE</span>
                  <span className="text-scale-blue font-bold text-lg md:text-xl ml-1">SMALL.AI</span>
                </div>
                <span className="text-gray-400 text-xs md:text-sm italic hidden md:block">Small Business Focused</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {!isAdmin && (
                <button
                  onClick={handleSaveAndExit}
                  className="flex items-center px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors font-semibold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save & Exit
                </button>
              )}

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 px-4 py-2 bg-scale-navy rounded-md hover:bg-scale-navy/80 transition-colors"
                  >
                    <User className="w-5 h-5 text-scale-blue" />
                    <span className="text-white max-w-[150px] truncate">{user.email}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-scale-navy rounded-md shadow-lg py-1 border border-gray-700 z-20">
                      {isAdmin && (
                        <button
                          onClick={() => {
                            navigate('/admin');
                            setShowUserMenu(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-scale-darker"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </button>
                      )}
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-scale-darker"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="flex items-center px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-white hover:text-scale-blue transition-colors"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-700 pt-4">
              {!isAdmin && (
                <button
                  onClick={handleSaveAndExit}
                  className="flex items-center w-full px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors font-semibold mb-2"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save & Exit
                </button>
              )}

              {user ? (
                <div className="space-y-2">
                  <div className="px-4 py-2 bg-scale-navy rounded-md">
                    <div className="flex items-center space-x-2">
                      <User className="w-5 h-5 text-scale-blue" />
                      <span className="text-white text-sm truncate">{user.email}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowMobileMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-scale-darker rounded-md"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-scale-darker rounded-md"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setShowMobileMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
    </>
  );
}
